<template>
  <ToolShell
    tool-name="Freelance contract template"
    h1="Free freelance contract template you can fill in and download."
    lede="Fill in the project, fee and terms, and get a plain-language freelance services agreement to review, edit and sign. Free, no signup, and nothing you type leaves your browser. It is a starting template, not legal advice."
    :trust="['No signup', 'Plain language', 'Word, PDF or text']"
    form-title="Agreement details"
    :upsell-points="[
      'Upload the agreement and send it for e-signature',
      'Proposals clients approve online',
      'Deposits and invoices from the same client record',
    ]"
    free-label="The free template"
    :free-bullets="[
      'A contract you edit and send yourself',
      'Signatures collected by email or on paper',
      'Invoices and payments tracked somewhere else',
    ]"
    upgrade-headline="A signed agreement starts the project. Workspace369 runs everything after it."
    :connected-bullets="[
      'Contracts sent for e-signature, with signing order and a sealed copy',
      'Proposals clients approve online, then the deposit invoice',
      'Every client’s messages, files and payments in one record',
      'A 14-day free trial, then plans from $29 a month',
    ]"
    slug="/tools/freelance-contract-template"
    :meta-description="metaDescription"
    :doc-brand="form.provider || 'Freelance Services Agreement'"
    :doc-subtitle="form.client ? `with ${form.client}` : 'Fill in the details to build it'"
    doc-kind="AGREEMENT"
    stamp="Template"
  >
    <template #form>
      <div class="ct-notice" role="note">
        <strong>Not legal advice.</strong> This template is a plain-language starting point. Laws differ by
        state and country; have a lawyer review it before you rely on it.
      </div>

      <div class="ta-field-row">
        <label class="ta-field">
          <span>Your name or business</span>
          <div class="ta-input"><input v-model="form.provider" type="text" placeholder="Rivera Studio LLC" maxlength="120" /></div>
        </label>
        <label class="ta-field">
          <span>Client name or business</span>
          <div class="ta-input"><input v-model="form.client" type="text" placeholder="Acme Wellness Inc." maxlength="120" /></div>
        </label>
      </div>

      <label class="ta-field">
        <span>Services</span>
        <div class="ta-input"><input v-model="form.services" type="text" placeholder="Brand identity design for Acme Wellness" maxlength="600" /></div>
      </label>

      <label class="ta-field">
        <span>Deliverables (one per line)</span>
        <div class="ta-input"><textarea v-model="form.deliverables" rows="3" placeholder="Logo suite&#10;Color palette and type system&#10;Brand guidelines PDF" maxlength="1500" /></div>
      </label>

      <label class="ta-field">
        <span>Not included (optional, one per line)</span>
        <div class="ta-input"><textarea v-model="form.exclusions" rows="2" placeholder="Website design&#10;Printing" maxlength="1000" /></div>
      </label>

      <div class="ta-field-row">
        <label class="ta-field">
          <span>Start date</span>
          <div class="ta-input"><input v-model="form.start" class="ct-date" type="date" /></div>
        </label>
        <label class="ta-field">
          <span>Target end date (optional)</span>
          <div class="ta-input"><input v-model="form.end" class="ct-date" type="date" /></div>
        </label>
      </div>

      <div class="ta-field-row">
        <div class="ta-field">
          <span id="ct-fee-label">Fee type</span>
          <ToolDropdown v-model="form.feeType" :options="feeOptions" aria-label="Fee type" labelledby="ct-fee-label" />
        </div>
        <div class="ta-field">
          <span id="ct-currency-label">Currency</span>
          <ToolDropdown v-model="form.currency" :options="currencyOptions" aria-label="Currency" labelledby="ct-currency-label" />
        </div>
      </div>

      <div class="ta-field-row">
        <label class="ta-field">
          <span>{{ amountLabel }}</span>
          <div class="ta-input"><input v-model="form.amount" type="text" inputmode="decimal" placeholder="6500" maxlength="12" /></div>
        </label>
        <label v-if="form.feeType === 'hourly'" class="ta-field">
          <span>Estimated hours (optional)</span>
          <div class="ta-input"><input v-model.number="form.hours" type="number" min="0" max="10000" inputmode="numeric" placeholder="40" /></div>
        </label>
        <label v-else class="ta-field">
          <span>Deposit %</span>
          <div class="ta-input"><input v-model.number="form.deposit" type="number" min="0" max="100" inputmode="numeric" placeholder="50" /></div>
        </label>
      </div>

      <div class="ta-field-row">
        <label class="ta-field">
          <span>Payment due (days)</span>
          <div class="ta-input"><input v-model.number="form.netDays" type="number" min="0" max="120" inputmode="numeric" placeholder="14" /></div>
        </label>
        <label class="ta-field">
          <span>Late fee % per month (optional)</span>
          <div class="ta-input"><input v-model.number="form.lateFee" type="number" min="0" max="10" step="0.5" inputmode="decimal" placeholder="1.5" /></div>
        </label>
      </div>

      <div class="ta-field-row">
        <label v-if="form.feeType === 'fixed'" class="ta-field">
          <span>Revision rounds included</span>
          <div class="ta-input"><input v-model.number="form.revisions" type="number" min="0" max="20" inputmode="numeric" placeholder="2" /></div>
        </label>
        <label class="ta-field">
          <span>Notice to end (days)</span>
          <div class="ta-input"><input v-model.number="form.noticeDays" type="number" min="0" max="180" inputmode="numeric" placeholder="14" /></div>
        </label>
      </div>

      <label v-if="form.feeType === 'fixed'" class="ta-field">
        <span>Cancellation fee, % of the unpaid fee (optional)</span>
        <div class="ta-input"><input v-model.number="form.killFee" type="number" min="0" max="100" inputmode="numeric" placeholder="25" /></div>
      </label>

      <div class="ta-field">
        <span id="ct-own-label">Who owns the final work</span>
        <ToolDropdown v-model="form.ownership" :options="ownershipOptions" aria-label="Ownership" labelledby="ct-own-label" />
      </div>

      <label class="ta-field">
        <span>Governing law (state or country)</span>
        <div class="ta-input"><input v-model="form.law" type="text" placeholder="the State of Florida" maxlength="80" /></div>
      </label>

      <label class="ct-check"><input v-model="form.portfolio" type="checkbox" /> Allow portfolio use once the work is public</label>
      <label class="ct-check"><input v-model="form.liabilityCap" type="checkbox" /> Include a limitation of liability clause</label>

      <button type="button" class="ta-download" @click="downloadWord">Download for Word (.doc)</button>
      <button type="button" class="ta-download" @click="printPdf">Print or save as PDF</button>
      <button type="button" class="ta-download" @click="copyText">{{ copied ? "Copied!" : "Copy as text" }}</button>

      <p class="ta-note">
        To collect signatures online, upload the PDF to
        <a href="https://docs.workspace369.com/v/2026.3/contracts/send-a-contract-for-signature/" target="_blank" rel="noopener">Workspace369 Contracts</a>,
        included from the Cadet plan.
      </p>
    </template>

    <template #preview>
      <ul v-if="contract.checklist.length" class="ct-checklist">
        <li v-for="item in contract.checklist" :key="item">{{ item }}</li>
      </ul>
      <!-- The agreement stays in English on every locale: it is what the exports contain. -->
      <article class="ct-doc" translate="no">
        <p class="ct-doc-notice">{{ contract.notice }}</p>
        <h3>{{ contract.title }}</h3>
        <section v-for="(section, i) in contract.sections" :key="section.heading">
          <h4>{{ i + 1 }}. {{ section.heading }}</h4>
          <template v-for="(p, j) in section.paragraphs" :key="j">
            <div v-if="Array.isArray(p)">
              <p>{{ p[0] }}</p>
              <ul><li v-for="item in p.slice(1)" :key="item">{{ item.replace(/^• /, "") }}</li></ul>
            </div>
            <p v-else>{{ p }}</p>
          </template>
        </section>
        <div class="ct-sigs">
          <div v-for="party in contract.signatures" :key="party">
            <strong>{{ party }}</strong>
            <span>Signature ____________________</span>
            <span>Date __________</span>
          </div>
        </div>
      </article>
    </template>

    <template #steps>
      <div><span>01</span><div><h2>Fill in the deal</h2><p>Who, what, when and how much: the scope, deliverables, fee, deposit and payment terms.</p></div></div>
      <div><span>02</span><div><h2>Choose the terms</h2><p>Revisions, notice to end, ownership, portfolio use and liability, in plain language.</p></div></div>
      <div><span>03</span><div><h2>Review, then sign</h2><p>Download it, have a lawyer check it for your state or country, and send it for signature.</p></div></div>
    </template>

    <ProposalVsContract />

    <section class="tool-faq">
      <div class="tool-faq-inner">
        <h2>Freelance contract FAQ</h2>
        <div class="tool-faq-list">
          <details v-for="item in faq" :key="item.q" class="tool-faq-item">
            <summary>{{ item.q }}</summary>
            <p>{{ item.a }}</p>
          </details>
        </div>
      </div>
    </section>

    <RelatedPages path="/tools/freelance-contract-template" title="Freelance contract template" />

    <Cta03 />
  </ToolShell>
</template>

<script setup>
import ToolDropdown from "~/components/tools/ToolDropdown.vue";
import ToolShell from "~/components/tools/ToolShell.vue";
import { buildContract, contractHtml, contractText, CURRENCIES } from "~/utils/freelance-contract.mjs";

const form = reactive({
  provider: "",
  client: "",
  services: "",
  deliverables: "",
  exclusions: "",
  start: "",
  end: "",
  feeType: "fixed",
  currency: "USD",
  amount: "",
  hours: "",
  deposit: 50,
  netDays: 14,
  lateFee: "",
  revisions: 2,
  noticeDays: 14,
  killFee: "",
  ownership: "assign",
  law: "",
  portfolio: true,
  liabilityCap: true,
});

const feeOptions = [
  { value: "fixed", label: "Fixed fee", hint: "one price for the project" },
  { value: "hourly", label: "Hourly", hint: "billed for time worked" },
  { value: "retainer", label: "Monthly retainer", hint: "ongoing work" },
];
const currencyOptions = CURRENCIES.map((code) => ({ value: code, label: code }));
const ownershipOptions = [
  { value: "assign", label: "Client owns it once paid", hint: "rights transfer on full payment" },
  { value: "license", label: "I keep it, client may use it", hint: "a license on full payment" },
];

const amountLabel = computed(() => ({ fixed: "Project fee", hourly: "Hourly rate", retainer: "Monthly fee" })[form.feeType]);
const today = () => new Date().toISOString().slice(0, 10);
const contract = computed(() => buildContract({ ...form, effective: form.start || "" }));

const fileName = computed(() => {
  const slug = `${form.client || "client"}-freelance-agreement`.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return slug || "freelance-agreement";
});

const downloadWord = () => {
  const blob = new Blob(["﻿", contractHtml(contract.value)], { type: "application/msword" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${fileName.value}-${today()}.doc`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const printPdf = () => {
  const win = window.open("", "_blank");
  if (!win) return;
  win.document.open();
  win.document.write(contractHtml(contract.value));
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 250);
};

const copied = ref(false);
const copyText = async () => {
  try {
    await navigator.clipboard.writeText(contractText(contract.value));
    copied.value = true;
    setTimeout(() => (copied.value = false), 1600);
  } catch { /* clipboard unavailable */ }
};

const faq = [
  {
    q: "Is this freelance contract template legally binding?",
    a: "A contract can be binding when both parties agree to clear terms and sign it, but whether a particular clause is enforceable depends on your state or country and the facts. This template is a plain-language starting point, not legal advice. Have a qualified lawyer review it before you rely on it, especially for large projects, work involving intellectual property, or clients in another country.",
  },
  {
    q: "What should a freelance contract include?",
    a: "Who the parties are, the services and deliverables, what is not included, the timeline, the fee and payment terms (deposit, due dates, late fees), how revisions and extra work are handled, who owns the work and when, confidentiality, how either side can end the agreement, and signatures with dates.",
  },
  {
    q: "Should I take a deposit?",
    a: "Many freelancers take 25% to 50% before starting fixed-fee work. It confirms the client is committed and covers your time if the project stalls. The template credits the deposit to the final invoice so the total stays clear.",
  },
  {
    q: "Who owns the work, the freelancer or the client?",
    a: "It depends on what you agree. A common approach is that the client owns the final deliverables once they have paid in full, while you keep your tools, templates and earlier work. Some freelancers keep ownership and give the client a license instead. Copyright rules differ by country, so check this clause with a lawyer.",
  },
  {
    q: "Can I sign a freelance contract electronically?",
    a: "In many places, including under the US ESIGN Act and the EU eIDAS Regulation, electronic signatures are generally accepted for this kind of agreement, with exceptions for some document types. Check the rules that apply to you. Workspace369 Contracts can send the PDF for e-signature from the Cadet plan.",
  },
  {
    q: "Is what I type stored?",
    a: "No. The agreement is built in your browser as you type, and nothing is sent to Workspace369 or saved after you close the page.",
  },
];

const metaTitle = "Free Freelance Contract Template (Download) | Workspace369";
const metaDescription =
  "Free freelance contract template: fill in scope, fee, deposit and terms, then download a plain-language agreement for Word or PDF. No signup. Not legal advice.";

useHead(() => ({
  title: metaTitle,
  meta: [
    { name: "description", content: metaDescription },
    { name: "twitter:title", content: metaTitle },
    { name: "twitter:description", content: metaDescription },
    { name: "twitter:card", content: "summary_large_image" },
    { property: "og:title", content: metaTitle },
    { property: "og:description", content: metaDescription },
  ],
  script: [
    {
      key: "tool-faq-schema",
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: { "@type": "Answer", text: item.a },
        })),
      }),
    },
  ],
}));
</script>

<style scoped>
.ct-notice {
  margin-bottom: 1rem;
  padding: 0.65rem 0.8rem;
  border: 1px solid #f59e0b;
  border-radius: 0.6rem;
  background: #fffbeb;
  color: #78350f;
  font-size: 0.85rem;
  line-height: 1.45;
}
.ta-input textarea {
  width: 100%;
  padding: 0.6rem 0;
  resize: vertical;
  border: 0;
  background: transparent;
  font: inherit;
  color: inherit;
  outline: none;
}
/* Native date inputs have a wide intrinsic size; without this they widen the whole grid on phones. */
.ct-date {
  min-width: 0;
  max-width: 100%;
}
.ct-check {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.35rem 0;
  color: #374151;
  font-size: 0.88rem;
}
.ct-checklist {
  margin: 0 0 0.75rem;
  padding: 0.6rem 0.8rem 0.6rem 1.8rem;
  border-radius: 0.6rem;
  background: #f0f9ff;
  color: #0c4a6e;
  font-size: 0.82rem;
}
.ct-doc {
  max-height: 34rem;
  overflow-y: auto;
  padding: 1rem 1.1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.65rem;
  background: #fff;
  color: #1f2937;
  font-family: Georgia, "Times New Roman", serif;
  font-size: 0.86rem;
  line-height: 1.55;
  overflow-wrap: anywhere;
}
.ct-doc-notice {
  padding: 0.5rem 0.65rem;
  border: 1px solid #f59e0b;
  background: #fffbeb;
  color: #78350f;
  font-family: inherit;
  font-size: 0.75rem;
}
.ct-doc h3 {
  margin: 0.75rem 0;
  text-align: center;
  font-size: 1.05rem;
}
.ct-doc h4 {
  margin: 0.8rem 0 0.3rem;
  font-size: 0.9rem;
}
.ct-doc p {
  margin: 0 0 0.4rem;
}
.ct-doc ul {
  margin: 0 0 0.4rem;
  padding-left: 1.2rem;
}
.ct-sigs {
  display: grid;
  gap: 0.9rem;
  margin-top: 1rem;
}
.ct-sigs div {
  display: grid;
  gap: 0.2rem;
}

/* Dark mode: the site's dark tokens (page #020617, card #0f172a, hairline #243247, text #f1f5f9 / #cbd5e1 / #94a3b8, accent #67e8f9). Light styles above are unchanged. */
.dark .ct-notice {
  background: rgb(251 191 36 / 0.12);
  color: #fcd34d;
}

.dark .ct-check {
  color: #cbd5e1;
}

.dark .ct-checklist {
  background: rgb(14 116 144 / 0.16);
  color: #67e8f9;
}

.dark .ct-doc {
  border: 1px solid #243247;
  background: #0f172a;
  color: #f1f5f9;
}

.dark .ct-doc-notice {
  background: rgb(251 191 36 / 0.12);
  color: #fcd34d;
}


</style>
